import { Box } from '@mui/material';
import { Theme, alpha, useTheme } from '@mui/material/styles';
import { RADIUS } from 'components/ui/tokens';
import { LABELS, LABEL_TINT_ALPHA } from '../constants';
import { WHATS_NEW_COPY } from '../content';
import type { Label, PostTheme } from '../types';

type Tone = 'primary' | 'info' | 'warning' | 'secondary';

const LABEL_TONES: Record<Label, Tone> = {
  [LABELS.new]: 'primary',
  [LABELS.improved]: 'info',
  [LABELS.beta]: 'warning',
  [LABELS.fixed]: 'secondary'
};

// The tone's darkest shade in light mode and lightest in dark: both stay above 4.5:1 on the tinted pill.
const toneSx = (theme: Theme, label: Label) => {
  const tone = theme.palette[LABEL_TONES[label]];
  return { color: theme.palette.mode === 'dark' ? tone.lighter : tone.dark, backgroundColor: alpha(tone.main, LABEL_TINT_ALPHA) };
};

// Inside a launch's own colours: its text on its accent, led by an accent dot.
const postThemeSx = (theme: PostTheme) => ({
  color: theme.text,
  backgroundColor: alpha(theme.accent, LABEL_TINT_ALPHA),
  '&::before': { content: '""', width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.accent }
});

/** A post's label (New, Improved, Beta, Fixed): in its tone, or in the post's own colours inside the popup. */
const LabelPill = ({ label, postTheme }: { label: Label; postTheme?: PostTheme }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.875,
        py: 0.375,
        borderRadius: RADIUS.tag,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        ...(postTheme ? postThemeSx(postTheme) : toneSx(theme, label))
      }}
    >
      {WHATS_NEW_COPY.labels[label]}
    </Box>
  );
};

export default LabelPill;
